import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  addIpawsAreaShape,
  addIpawsPolygonVertex,
  clearIpawsAreaShapes,
  moveIpawsPolygonVertex,
  removeIpawsAreaShape,
  updateIpawsAreaShape,
} from '../../../state/slices/alertCreatorSlice';
import {
  createIpawsAreaShapeId,
  DEFAULT_IPAWS_CIRCLE_RADIUS_KM,
  DEFAULT_IPAWS_SQUARE_SIDE_LENGTH_KM,
  getIpawsAreaMetrics,
  getIpawsAreaShapeLabel,
  getIpawsAreaShapes,
  getIpawsPolygonRequiredPointCapacity,
  IPAWS_AREA_LIMITS,
  IPAWS_AREA_TYPES,
  NWEM_MAXIMUM_DRAWN_POLYGON_VERTEX_COUNT,
} from '../../../utils/ipawsArea.utils';
import { isValidMapCoordinate } from '../utils/alertMapEditor.utils';
import { getIpawsPolygonFindings } from '../../../utils/ipawsPolygon.utils';

const getAreaToolPointCost = (toolType) => {
  if (toolType === IPAWS_AREA_TYPES.SQUARE) return 5;
  if (toolType === IPAWS_AREA_TYPES.POLYGON) return 4;
  return 1;
};

const getPlacementPreviewShape = ({ activeTool, pointerLocation, selectedShape }) => {
  if (!pointerLocation) return null;

  if (activeTool === IPAWS_AREA_TYPES.CIRCLE) {
    return {
      id: 'ipaws-area-preview',
      type: IPAWS_AREA_TYPES.CIRCLE,
      center: pointerLocation,
      radiusKm:
        (selectedShape?.type === IPAWS_AREA_TYPES.CIRCLE ? selectedShape.radiusKm : null) ||
        DEFAULT_IPAWS_CIRCLE_RADIUS_KM,
    };
  }

  if (activeTool === IPAWS_AREA_TYPES.SQUARE) {
    return {
      id: 'ipaws-area-preview',
      type: IPAWS_AREA_TYPES.SQUARE,
      center: pointerLocation,
      sideLengthKm:
        (selectedShape?.type === IPAWS_AREA_TYPES.SQUARE ? selectedShape.sideLengthKm : null) ||
        DEFAULT_IPAWS_SQUARE_SIDE_LENGTH_KM,
    };
  }

  return null;
};

/**
 * Owns one IPAWS map-editing session. It keeps temporary pointer and drag previews local, then
 * saves only completed shape changes to the alert draft.
 *
 * @param {Object} alertData Current IPAWS alert draft.
 * @returns {{ state: Object, actions: Object }} Values to display and actions the editor can run.
 */
const useIpawsAreaEditor = (alertData, dispatchAreaAction) => {
  const reduxDispatch = useDispatch();
  const dispatch = dispatchAreaAction ?? reduxDispatch;
  const [activeTool, setActiveTool] = useState(null);
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [pointerLocation, setPointerLocation] = useState(null);
  const [dragShapePreview, setDragShapePreview] = useState(null);
  const [editMode, setEditMode] = useState('move');
  const [historyVersion, setHistoryVersion] = useState(0);
  const historyRef = useRef({ areaId: null, undo: [], redo: [], original: null });
  const areaShapes = useMemo(() => getIpawsAreaShapes(alertData), [alertData]);
  const isNwemSelected = alertData?.deliveryChannels?.includes('NWEM');

  // Replace only the shape being dragged with its temporary values. Other shapes still come from
  // the saved draft.
  const displayedAreaShapes = useMemo(() => {
    if (!dragShapePreview) return areaShapes;

    return areaShapes.map((shape) =>
      shape.id === dragShapePreview.areaId ? { ...shape, ...dragShapePreview.updates } : shape,
    );
  }, [areaShapes, dragShapePreview]);

  const { shapeCount, pointCount } = getIpawsAreaMetrics(areaShapes);
  const selectedShape = areaShapes.find((shape) => shape.id === selectedShapeId);
  const canInsertPolygonVertex =
    selectedShape?.type === IPAWS_AREA_TYPES.POLYGON &&
    pointCount + 1 <= IPAWS_AREA_LIMITS.maximumPointCount &&
    (!isNwemSelected ||
      (selectedShape.coordinates?.length ?? 0) < NWEM_MAXIMUM_DRAWN_POLYGON_VERTEX_COUNT);
  const displayedSelectedShape = displayedAreaShapes.find((shape) => shape.id === selectedShapeId);
  const selectedShapeIndex = areaShapes.findIndex((shape) => shape.id === selectedShapeId);
  const selectedShapeLabel = selectedShape
    ? getIpawsAreaShapeLabel(selectedShape, selectedShapeIndex)
    : '';
  const cloneShape = useCallback((shape) => (shape ? JSON.parse(JSON.stringify(shape)) : null), []);

  const resetHistory = useCallback(
    (shape) => {
      historyRef.current = {
        areaId: shape?.id ?? null,
        undo: [],
        redo: [],
        original: cloneShape(shape),
      };
      setHistoryVersion((version) => version + 1);
    },
    [cloneShape],
  );

  const rememberShape = useCallback(
    (shape) => {
      if (!shape) return;
      if (historyRef.current.areaId !== shape.id) resetHistory(shape);
      historyRef.current.undo.push(cloneShape(shape));
      historyRef.current.redo = [];
      setHistoryVersion((version) => version + 1);
    },
    [cloneShape, resetHistory],
  );
  const placementPreviewShape = useMemo(
    () => getPlacementPreviewShape({ activeTool, pointerLocation, selectedShape }),
    [activeTool, pointerLocation, selectedShape],
  );

  const clearDragShapePreview = useCallback(() => {
    setDragShapePreview(null);
  }, []);

  const previewShapeUpdate = useCallback((areaId, updates) => {
    setDragShapePreview({ areaId, updates });
  }, []);

  useEffect(() => {
    // A selected shape may disappear after Delete, Clear, or an outside draft update.
    if (selectedShapeId && !areaShapes.some((shape) => shape.id === selectedShapeId)) {
      clearDragShapePreview();
      setSelectedShapeId(null);
    }
  }, [areaShapes, clearDragShapePreview, selectedShapeId]);

  const finishActiveTool = useCallback(() => {
    clearDragShapePreview();
    setActiveTool(null);
    setPointerLocation(null);
  }, [clearDragShapePreview]);

  const cancelActiveTool = useCallback(() => {
    // A polygon with fewer than three drawn points cannot form an area, so cancel removes it.
    if (
      activeTool === IPAWS_AREA_TYPES.POLYGON &&
      selectedShape &&
      (selectedShape.coordinates?.length ?? 0) < 3
    ) {
      dispatch(removeIpawsAreaShape(selectedShape.id));
      setSelectedShapeId(null);
    }

    finishActiveTool();
  }, [activeTool, dispatch, finishActiveTool, selectedShape]);

  useEffect(() => {
    if (!activeTool) return undefined;

    const cancelWithEscape = (event) => {
      if (event.key === 'Escape') cancelActiveTool();
    };

    window.addEventListener('keydown', cancelWithEscape);
    return () => window.removeEventListener('keydown', cancelWithEscape);
  }, [activeTool, cancelActiveTool]);

  const startAreaTool = useCallback(
    (toolType) => {
      const pointCost = getAreaToolPointCost(toolType);
      const canAddShape =
        shapeCount < IPAWS_AREA_LIMITS.maximumShapeCount &&
        pointCount + pointCost <= IPAWS_AREA_LIMITS.maximumPointCount;
      if (!canAddShape) return;

      if (toolType === IPAWS_AREA_TYPES.POLYGON) setSelectedShapeId(null);

      clearDragShapePreview();
      setActiveTool(toolType);
    },
    [clearDragShapePreview, pointCount, shapeCount],
  );

  const placeShape = useCallback(
    (latlng) => {
      if (!activeTool || !isValidMapCoordinate(latlng)) return;

      const normalizedCenter = { lat: Number(latlng.lat), lng: Number(latlng.lng) };

      if (activeTool === IPAWS_AREA_TYPES.POLYGON) {
        if (!selectedShapeId) {
          // The first click creates the polygon. Later clicks add points to this same ID.
          if (
            shapeCount >= IPAWS_AREA_LIMITS.maximumShapeCount ||
            pointCount + 4 > IPAWS_AREA_LIMITS.maximumPointCount
          ) {
            return;
          }

          const areaId = createIpawsAreaShapeId();
          dispatch(
            addIpawsAreaShape({
              id: areaId,
              type: IPAWS_AREA_TYPES.POLYGON,
              coordinates: [normalizedCenter],
              lastInserted: [normalizedCenter],
            }),
          );
          setSelectedShapeId(areaId);
          return;
        }

        // Keep enough space for the repeated closing point and the minimum complete polygon.
        if (
          isNwemSelected &&
          (selectedShape?.coordinates?.length ?? 0) >= NWEM_MAXIMUM_DRAWN_POLYGON_VERTEX_COUNT
        ) {
          return;
        }

        const requiredPointCapacity = getIpawsPolygonRequiredPointCapacity(selectedShape);
        if (pointCount + requiredPointCapacity > IPAWS_AREA_LIMITS.maximumPointCount) return;

        const nextCoordinates = [...(selectedShape?.coordinates ?? []), normalizedCenter];
        // The first two points cannot intersect. From the third point onward, reject invalid
        // geometry before it reaches Redux so a bowtie/overlap never renders on the map.
        if (nextCoordinates.length >= 3 && getIpawsPolygonFindings(nextCoordinates).length) return;

        dispatch(
          addIpawsPolygonVertex({
            areaId: selectedShapeId,
            coordinate: normalizedCenter,
          }),
        );
        return;
      }

      const pointCost = getAreaToolPointCost(activeTool);
      if (
        shapeCount >= IPAWS_AREA_LIMITS.maximumShapeCount ||
        pointCount + pointCost > IPAWS_AREA_LIMITS.maximumPointCount
      ) {
        return;
      }

      const areaId = createIpawsAreaShapeId();
      // Reuse the last selected shape's size when placing another shape of the same type.
      const nextShape =
        activeTool === IPAWS_AREA_TYPES.CIRCLE
          ? {
              id: areaId,
              type: IPAWS_AREA_TYPES.CIRCLE,
              center: normalizedCenter,
              radiusKm:
                selectedShape?.type === IPAWS_AREA_TYPES.CIRCLE
                  ? selectedShape.radiusKm
                  : DEFAULT_IPAWS_CIRCLE_RADIUS_KM,
            }
          : {
              id: areaId,
              type: IPAWS_AREA_TYPES.SQUARE,
              center: normalizedCenter,
              sideLengthKm:
                selectedShape?.type === IPAWS_AREA_TYPES.SQUARE
                  ? selectedShape.sideLengthKm
                  : DEFAULT_IPAWS_SQUARE_SIDE_LENGTH_KM,
            };

      dispatch(addIpawsAreaShape(nextShape));
      setSelectedShapeId(areaId);
      finishActiveTool();
    },
    [
      activeTool,
      dispatch,
      finishActiveTool,
      isNwemSelected,
      pointCount,
      selectedShape,
      selectedShapeId,
      shapeCount,
    ],
  );

  const previewPolygonVertexMove = useCallback(
    (vertexIndex, newPosition) => {
      if (
        selectedShape?.type !== IPAWS_AREA_TYPES.POLYGON ||
        vertexIndex < 0 ||
        vertexIndex >= (selectedShape.coordinates?.length ?? 0)
      ) {
        return;
      }

      const coordinates = [...selectedShape.coordinates];
      coordinates[vertexIndex] = newPosition;
      previewShapeUpdate(selectedShape.id, { coordinates });
    },
    [previewShapeUpdate, selectedShape],
  );

  const commitPolygonVertexMove = useCallback(
    (vertexIndex, newPosition) => {
      if (selectedShape?.type !== IPAWS_AREA_TYPES.POLYGON) return false;

      const coordinates = [...selectedShape.coordinates];
      coordinates[vertexIndex] = newPosition;
      if (getIpawsPolygonFindings(coordinates).length) {
        clearDragShapePreview();
        return false;
      }

      clearDragShapePreview();
      rememberShape(selectedShape);
      dispatch(
        moveIpawsPolygonVertex({
          areaId: selectedShape.id,
          vertexIndex,
          newPosition,
        }),
      );
      return true;
    },
    [clearDragShapePreview, dispatch, rememberShape, selectedShape],
  );

  const previewSelectedShapeUpdate = useCallback(
    (updates) => {
      if (selectedShape && updates) previewShapeUpdate(selectedShape.id, updates);
    },
    [previewShapeUpdate, selectedShape],
  );

  const commitSelectedShapeUpdate = useCallback(
    (updates) => {
      if (!selectedShape || !updates) return false;
      if (
        selectedShape.type === IPAWS_AREA_TYPES.POLYGON &&
        Array.isArray(updates.coordinates) &&
        getIpawsPolygonFindings(updates.coordinates).length
      ) {
        clearDragShapePreview();
        return false;
      }

      clearDragShapePreview();
      rememberShape(selectedShape);
      dispatch(updateIpawsAreaShape({ areaId: selectedShape.id, updates }));
      return true;
    },
    [clearDragShapePreview, dispatch, rememberShape, selectedShape],
  );

  const selectShape = useCallback(
    (areaId) => {
      // Clicking the polygon being drawn should add a point instead of selecting it.
      if (activeTool === IPAWS_AREA_TYPES.POLYGON && areaId === selectedShapeId) return;

      cancelActiveTool();
      setSelectedShapeId(areaId);
      const nextSelectedShape = areaShapes.find((shape) => shape.id === areaId);
      setEditMode(nextSelectedShape?.type === IPAWS_AREA_TYPES.POLYGON ? 'reshape' : 'move');
      resetHistory(nextSelectedShape);
    },
    [activeTool, areaShapes, cancelActiveTool, resetHistory, selectedShapeId],
  );

  const continuePolygon = useCallback(() => {
    if (
      isNwemSelected &&
      (selectedShape?.coordinates?.length ?? 0) >= NWEM_MAXIMUM_DRAWN_POLYGON_VERTEX_COUNT
    ) {
      return;
    }
    setActiveTool(IPAWS_AREA_TYPES.POLYGON);
  }, [isNwemSelected, selectedShape]);

  const updateSelectedShape = useCallback(
    (updates) => {
      rememberShape(selectedShape);
      dispatch(updateIpawsAreaShape({ areaId: selectedShapeId, updates }));
    },
    [dispatch, rememberShape, selectedShape, selectedShapeId],
  );

  const restoreShape = useCallback(
    (shape) => {
      if (!shape) return;
      clearDragShapePreview();
      // Older snapshots can predate the first rename, so explicitly restore their default name.
      dispatch(
        updateIpawsAreaShape({ areaId: shape.id, updates: { ...shape, name: shape.name ?? '' } }),
      );
    },
    [clearDragShapePreview, dispatch],
  );

  const undoShapeEdit = useCallback(() => {
    const previous = historyRef.current.undo.pop();
    if (!previous || !selectedShape) return;
    historyRef.current.redo.push(cloneShape(selectedShape));
    restoreShape(previous);
    setHistoryVersion((version) => version + 1);
  }, [cloneShape, restoreShape, selectedShape]);

  const redoShapeEdit = useCallback(() => {
    const next = historyRef.current.redo.pop();
    if (!next || !selectedShape) return;
    historyRef.current.undo.push(cloneShape(selectedShape));
    restoreShape(next);
    setHistoryVersion((version) => version + 1);
  }, [cloneShape, restoreShape, selectedShape]);

  const cancelShapeEdits = useCallback(() => {
    restoreShape(historyRef.current.original);
    resetHistory(historyRef.current.original);
  }, [resetHistory, restoreShape]);

  useEffect(() => {
    if (!selectedShapeId || activeTool) return undefined;
    const cancelEditsWithEscape = (event) => {
      if (event.key === 'Escape' && historyRef.current.undo.length) cancelShapeEdits();
    };
    window.addEventListener('keydown', cancelEditsWithEscape);
    return () => window.removeEventListener('keydown', cancelEditsWithEscape);
  }, [activeTool, cancelShapeEdits, selectedShapeId]);

  const insertPolygonVertex = useCallback(
    (insertIndex, coordinate) => {
      if (selectedShape?.type !== IPAWS_AREA_TYPES.POLYGON) return;
      const coordinates = selectedShape.coordinates ?? [];
      const isAtCapLimit = pointCount + 1 > IPAWS_AREA_LIMITS.maximumPointCount;
      const isAtNwemLimit =
        isNwemSelected && coordinates.length >= NWEM_MAXIMUM_DRAWN_POLYGON_VERTEX_COUNT;
      if (
        coordinates.length < 2 ||
        !Number.isInteger(insertIndex) ||
        !isValidMapCoordinate(coordinate) ||
        isAtCapLimit ||
        isAtNwemLimit
      )
        return;
      const nextCoordinates = [...coordinates];
      nextCoordinates.splice(insertIndex, 0, coordinate);
      if (getIpawsPolygonFindings(nextCoordinates).length) return;
      updateSelectedShape({ coordinates: nextCoordinates, lastInserted: nextCoordinates });
    },
    [isNwemSelected, pointCount, selectedShape, updateSelectedShape],
  );

  const removeSelectedPolygonVertex = useCallback(
    (vertexIndex) => {
      if (
        selectedShape?.type !== IPAWS_AREA_TYPES.POLYGON ||
        !Number.isInteger(vertexIndex) ||
        selectedShape.coordinates.length <= 3
      )
        return;
      const nextCoordinates = selectedShape.coordinates.filter(
        (_coordinate, index) => index !== vertexIndex,
      );
      if (getIpawsPolygonFindings(nextCoordinates).length) return;
      updateSelectedShape({ coordinates: nextCoordinates, lastInserted: nextCoordinates });
    },
    [selectedShape, updateSelectedShape],
  );

  const deleteSelectedShape = useCallback(() => {
    dispatch(removeIpawsAreaShape(selectedShapeId));
    setSelectedShapeId(null);
    finishActiveTool();
  }, [dispatch, finishActiveTool, selectedShapeId]);

  const clearShapes = useCallback(() => {
    dispatch(clearIpawsAreaShapes());
    setSelectedShapeId(null);
    finishActiveTool();
  }, [dispatch, finishActiveTool]);

  return {
    state: {
      activeTool,
      areaShapes,
      displayedAreaShapes,
      displayedSelectedShape,
      placementPreviewShape,
      selectedShape,
      selectedShapeId,
      selectedShapeLabel,
      editMode,
      canUndo: historyRef.current.undo.length > 0,
      canRedo: historyRef.current.redo.length > 0,
      canInsertPolygonVertex,
      historyVersion,
    },
    actions: {
      cancelActiveTool,
      clearShapes,
      commitPolygonVertexMove,
      commitSelectedShapeUpdate,
      continuePolygon,
      deleteSelectedShape,
      finishActiveTool,
      placeShape,
      previewPolygonVertexMove,
      previewSelectedShapeUpdate,
      selectShape,
      setPointerLocation,
      startAreaTool,
      updateSelectedShape,
      setEditMode,
      undoShapeEdit,
      redoShapeEdit,
      cancelShapeEdits,
      insertPolygonVertex,
      removeSelectedPolygonVertex,
    },
  };
};

export default useIpawsAreaEditor;
